import React, { Component, Fragment, ReactNode } from 'react';
import { PageableParams, PageableResponse } from '@app/api';
import { genericMemo } from '@app/helpers';
import { Empty, Loader, Pagination } from '@app/components';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { IconGrid, IconInline } from '@app/icons';
import { EventEmitter } from 'EventEmitter';

export interface RenderFilterProps<T> {
  onSubmit: (data: T) => void;
  pending: boolean;
  okedParentNumber?: number;
}

interface Props<T, F> {
  getData: (params: PageableParams, filter?: F) => Promise<PageableResponse<T>>;
  renderItem: (item: T, horizontal?: boolean) => ReactNode;
  initialFilter?: F;
  renderFilter?: (filterProps: RenderFilterProps<F>) => ReactNode;
  horizontal?: boolean;
  variant?: 'default' | 'paper';
  emptyIcon?: ReactNode;
  emptyTitle?: string | React.ReactNode;
  columns?: number;
  title?: string | ReactNode;
  controls?: ReactNode[];
  viewControl?: boolean;
  refreshEventName?: string;
  gap?: number;
  columnsClass?: string;
}

interface State<T, F> {
  pending: boolean;
  error: boolean;
  viewInline: boolean;
  page: number;
  totalPages: number;
  totalItems: number;
  filter: F;
  items: T[];
}

class SearchTemplate<T extends { id: string }, F = unknown> extends Component<
  Props<T, F>,
  State<T, F>
> {
  eventEmitter: Function | null = null;

  state: State<T, F> = {
    pending: true,
    error: false,
    viewInline: Boolean(this.props.horizontal),
    page: 1,
    totalPages: 0,
    totalItems: 0,
    filter: this.props.initialFilter || ({} as F),
    items: [],
  };

  async componentDidMount() {
    const { refreshEventName } = this.props;
    await this.getData();
    if (refreshEventName) {
      this.eventEmitter = EventEmitter.subscribe(refreshEventName, async () => {
        await this.getData();
      });
    }
  }

  componentWillUnmount() {
    if (this.eventEmitter) {
      this.eventEmitter();
    }
  }

  getData = async () => {
    try {
      const { page, filter } = this.state;
      const { data, meta } = await this.props.getData(
        {
          page,
          perPage: 6,
        },
        filter
      );
      this.setState({
        items: data,
        totalPages: meta.totalPages,
        totalItems: meta.totalItems,
        pending: false,
        error: false,
      });
    } catch (e) {
      this.setState({
        pending: false,
        error: true,
      });
    }
  };

  onClickControl =
    (value: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      this.setState({
        viewInline: value,
      });
    };

  onSubmitFilter = (filter: F) => {
    this.setState(
      {
        filter,
        items: [],
        page: 1,
        pending: true,
      },
      async () => {
        await this.getData();
      }
    );
  };

  onChangePage = (page: number) => {
    this.setState(
      {
        page,
      },
      async () => {
        await this.getData();
      }
    );
  };

  renderFilter = () => {
    const { renderFilter } = this.props;
    const { pending } = this.state;
    if (!renderFilter) {
      return null;
    }
    return renderFilter({
      onSubmit: this.onSubmitFilter,
      pending,
    });
  };

  render() {
    const {
      renderFilter,
      renderItem,
      variant,
      emptyIcon,
      emptyTitle,
      columns = 3,
      title,
      controls,
      viewControl = true,
      gap = 16,
      columnsClass = 'grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    } = this.props;
    const { pending, viewInline, items, page, totalPages, totalItems } =
      this.state;
    return (
      <div className="w-full flex flex-col gap-4">
        {renderFilter && (
          <Card className="w-full bg-white rounded-xl shadow-sm">
            <CardContent>{this.renderFilter()}</CardContent>
          </Card>
        )}
        <Card className="w-full bg-white rounded-xl shadow-sm">
          <CardContent className="flex flex-col gap-4">
            {(!!title || !!controls) && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                  {!!title && (
                    <div className="font-semibold text-xl">{title}</div>
                  )}
                  {!!controls && (
                    <div className="flex gap-2">
                      {controls.map((control, i) => (
                        <Fragment key={i}>{control}</Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {viewControl && (
              <div className="hidden sm:flex gap-2 mb-4">
                <Button
                  variant={!viewInline ? 'default' : 'outline'}
                  onClick={this.onClickControl(false)}
                  size="icon"
                >
                  <IconGrid />
                </Button>
                <Button
                  variant={viewInline ? 'default' : 'outline'}
                  onClick={this.onClickControl(true)}
                  size="icon"
                >
                  <IconInline />
                </Button>
              </div>
            )}
            {pending && (
              <div className="flex justify-center items-center min-h-[120px]">
                <Loader />
              </div>
            )}
            {!pending && items.length > 0 && (
              <div
                className={`grid w-full ${
                  viewInline ? 'grid-cols-1 gap-4' : columnsClass + ' gap-3'
                }`}
              >
                {items.map((item) => (
                  <Fragment key={item.id}>
                    {renderItem(item, viewInline)}
                  </Fragment>
                ))}
              </div>
            )}
            {!pending && items.length === 0 && (
              <div className="flex justify-center items-center min-h-[120px]">
                <Empty title={emptyTitle} icon={emptyIcon} />
              </div>
            )}
            <div className="mt-4">
              <Pagination
                initialPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                onChange={this.onChangePage}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default genericMemo(SearchTemplate);
